import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-[hsl(214_100%_55%)] shadow-glow [box-shadow:inset_0_1px_0_hsl(0_0%_100%/0.18),var(--tw-shadow)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-primary/60 bg-transparent text-foreground hover:bg-primary/10 hover:border-primary",
        secondary: "bg-white/[0.06] text-foreground hover:bg-white/10 border border-white/10",
        ghost: "text-muted-foreground hover:bg-white/[0.05] hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Enterprise variants (kept names for back-compat)
        enterprise: "bg-primary text-primary-foreground hover:bg-[hsl(214_100%_55%)] shadow-glow [box-shadow:inset_0_1px_0_hsl(0_0%_100%/0.18),var(--tw-shadow)]",
        "enterprise-outline": "border border-primary/60 bg-transparent text-foreground hover:bg-primary/10 hover:border-primary",
        "enterprise-ghost": "text-muted-foreground hover:text-foreground hover:bg-white/[0.05]",
        "hero": "bg-primary text-primary-foreground hover:bg-[hsl(214_100%_55%)] shadow-glow hover:shadow-[0_0_50px_-6px_hsl(214_100%_44%/0.9)] font-bold [box-shadow:inset_0_1px_0_hsl(0_0%_100%/0.2),var(--tw-shadow)]",
        "hero-outline": "border border-white/30 bg-transparent text-white hover:bg-white/[0.07] hover:border-primary",
        // Gold accent CTA (use sparingly)
        gold: "bg-accent text-accent-foreground hover:bg-[hsl(47_88%_38%)] shadow-glow-purple [box-shadow:inset_0_1px_0_hsl(0_0%_100%/0.18),var(--tw-shadow)]",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 px-4",
        lg: "h-12 px-7 text-base",
        xl: "h-13 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
